import { useState, useEffect, useCallback, useRef, useMemo, startTransition } from 'react';
import socketManager from '../utils/socketManager';
import { debounce } from 'lodash';

const API_URL = '';  // Use relative URLs

// Module-level cache to persist across component unmounts/remounts
const globalAnalysisCache = new Map();

// Deep comparison function to prevent unnecessary state updates
const isEqual = (obj1, obj2) => {
  // Handle primitives
  if (obj1 === obj2) return true;
  
  // Handle null/undefined
  if (obj1 == null || obj2 == null) return obj1 === obj2;
  
  // Handle different types or non-objects
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;
  
  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    return obj1.every((val, i) => isEqual(val, obj2[i]));
  }
  
  // Handle objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  return keys1.every(key => 
    Object.prototype.hasOwnProperty.call(obj2, key) && 
    isEqual(obj1[key], obj2[key])
  );
};

/**
 * Hook to fetch analysis data for a specific view and pillar
 * @param {string} viewId - The view ID
 * @param {string} pillarId - The pillar ID
 * @returns {Object} The analysis data and loading state
 */
export function useAnalysisData(viewId, pillarId) {
  const [analysisData, setAnalysisData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchInProgressRef = useRef(false);
  const lastAnalysisDataRef = useRef(null);
  const prevParamsRef = useRef({ viewId: null, pillarId: null });
  const prevDataId = useRef(null);
  const socketRef = useRef(null);
  const lastSocketUpdateRef = useRef(0);
  const lastFetchStartedAtRef = useRef(0);
  
  // Memoize cache key
  const cacheKey = useMemo(() => `${viewId}_${pillarId}`, [viewId, pillarId]);

  // Track data changes for development
  useEffect(() => {
    if (analysisData && analysisData.id !== prevDataId.current) {
      prevDataId.current = analysisData.id;
    }
  }, [analysisData, viewId, pillarId, isLoading]);

  const updateAnalysisData = useCallback((newData) => {
    if (isEqual(newData, lastAnalysisDataRef.current)) {
      return;
    }
    
    lastAnalysisDataRef.current = newData;
    
    // Update cache if we have valid data
    if (newData && viewId && pillarId) {
      globalAnalysisCache.set(`${viewId}_${pillarId}`, newData);
    }
    
    startTransition(() => {
      setAnalysisData(newData);
    });
  }, [viewId, pillarId]);

  const startLoading = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);
  }, [isLoading]);

  const finishLoading = useCallback((newData) => {
    startTransition(() => {
      updateAnalysisData(newData);
      setIsLoading(false);
    });
    fetchInProgressRef.current = false;
  }, [updateAnalysisData]);

  const fetchData = useCallback(async (forceRefetch = false) => {
    if (fetchInProgressRef.current) return;
    if (!viewId || !pillarId) {
      updateAnalysisData(null);
      setIsLoading(false); // Ensure loading is false if no params
      setError(null); // Clear any previous errors
      return;
    }

    // Check cache first
    if (!forceRefetch) {
      const cachedData = globalAnalysisCache.get(cacheKey);
      if (cachedData) {
        updateAnalysisData(cachedData);
        setIsLoading(false);
        return;
      }
    }

    lastFetchStartedAtRef.current = Date.now();
    fetchInProgressRef.current = true;
    startLoading();
    setError(null);
    try {
      const url = `/api/analysis/by-id/${pillarId}/${viewId}`;
      const analysisResponse = await fetch(url);
      if (analysisResponse.status === 404) {
        setError('Analysis data not found for the selected pillar.');
        finishLoading(null);
        return;
      }
      if (!analysisResponse.ok) {
        const errorText = await analysisResponse.text();
        throw new Error(`Failed to fetch analysis data: ${analysisResponse.statusText} (Status: ${analysisResponse.status})${errorText ? ` - ${errorText}` : ''}`);
      }
      const analysisResult = await analysisResponse.json();
      const latestAnalysis = Array.isArray(analysisResult)
        ? (analysisResult.length > 0 ? analysisResult[0] : null)
        : (analysisResult && Object.keys(analysisResult).length > 0 ? analysisResult : null);
      
      // Additional checks to ensure data consistency
      if (latestAnalysis && latestAnalysis.view_id && latestAnalysis.view_id.toString() !== viewId.toString()) {
        console.warn('[Analysis Fetch] Mismatched view_id, discarding data');
        finishLoading(null);
        return;
      }
      // if (latestAnalysis && !latestAnalysis.content) { // Allow empty content, but data should still be set
      //   console.warn('[Analysis Fetch] Analysis content is empty, treating as no data');
      //   finishLoading(null);
      //   return;
      // }
      finishLoading(latestAnalysis);
    } catch (err) {
      startTransition(() => {
        setError(err.message);
        setAnalysisData(null); // Clear data on error
        setIsLoading(false);
      });
      fetchInProgressRef.current = false;
    }
  }, [viewId, pillarId, startLoading, finishLoading, updateAnalysisData, cacheKey]);

  // Fetch a specific analysis by ID
  const loadAnalysisById = useCallback(async (analysisId) => {
    if (!analysisId) return;
    
    startLoading();
    setError(null);
    try {
      const response = await fetch(`/api/analysis/${analysisId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch analysis: ${response.statusText}`);
      }
      const result = await response.json();
      if (result.success) {
        finishLoading(result.data);
      } else {
        throw new Error(result.message || 'Failed to load analysis');
      }
    } catch (err) {
      startTransition(() => {
        setError(err.message);
        setIsLoading(false);
      });
    }
  }, [startLoading, finishLoading]);

  // Debounced fetch data is no longer strictly necessary without WebSocket triggers, 
  // but keeping it in case direct calls to refreshData are made rapidly.
  const debouncedFetchData = useCallback(
    debounce(() => {
      fetchData(true); // Always force refetch on manual refresh
    }, 150),
    [fetchData]
  );

  useEffect(() => {
    const prevParams = prevParamsRef.current;
    const paramsChanged = prevParams.viewId !== viewId || prevParams.pillarId !== pillarId;
    
    if (paramsChanged) {
    prevParamsRef.current = { viewId, pillarId };
        setError(null); // Clear error on param change
        setIsLoading(true); // Set loading true immediately
        updateAnalysisData(null); // Clear previous data immediately
    if (viewId && pillarId) {
            fetchData(); // Fetch new data
        } else {
            setIsLoading(false); // If params become invalid, stop loading
        }
    } else if (!viewId || !pillarId) {
        // If params are invalid (e.g., cleared), ensure data is cleared and loading is false
      updateAnalysisData(null);
        setIsLoading(false);
        setError(null);
    }
  }, [viewId, pillarId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live updates via WebSocket using shared socketManager
  useEffect(() => {
    if (!viewId || !pillarId) {
      return;
    }

    // Get shared socket for /analysis namespace (multiplexed)
    const socket = socketManager.getSocket('/analysis');
    socketRef.current = socket;

    const handleAnalysisUpdate = (payload) => {
      console.log('[useAnalysisData] Received analysis_update event:', {
        payloadId: payload?.id,
        payloadViewId: payload?.view_id,
        payloadPillarId: payload?.pillar_id,
        expectedViewId: viewId,
        expectedPillarId: pillarId,
        hasContent: !!payload?.content,
        contentLength: payload?.content?.length || 0
      });
      if (!payload) return;
      const payloadView = payload.view_id ?? payload.viewId;
      const payloadPillar = payload.pillar_id ?? payload.pillarId;
      
      // CRITICAL: Reject updates without valid view_id AND pillar_id
      // Empty broadcasts were clearing valid analysis data
      if (payloadView === undefined || payloadPillar === undefined) {
        console.log('[useAnalysisData] Skipping - missing view_id or pillar_id:', { payloadView, payloadPillar });
        return;
      }
      
      if (Number(payloadView) !== Number(viewId)) {
        console.log('[useAnalysisData] Skipping - view mismatch:', { payloadView, viewId });
        return;
      }
      if (Number(payloadPillar) !== Number(pillarId)) {
        console.log('[useAnalysisData] Skipping - pillar mismatch:', { payloadPillar, pillarId });
        return;
      }
      console.log('[useAnalysisData] Updating analysis data from WebSocket');
      // Directly update data instead of refetching
      finishLoading(payload);
    };

    const handleDataUpdate = (payload) => {
      console.log('[useAnalysisData] Received data_update event:', {
        type: payload?.type,
        hasData: !!payload?.data,
        expectedViewId: viewId,
        expectedPillarId: pillarId
      });
      if (!payload || payload.type !== 'analysis') return;
      const rows = Array.isArray(payload.data) ? payload.data : [payload.data];
      const matchingRow = rows.find((row) => {
        const rowView = row?.view_id ?? row?.viewId;
        const rowPillar = row?.pillar_id ?? row?.pillarId;
        return (
          Number(rowView) === Number(viewId) &&
          Number(rowPillar) === Number(pillarId)
        );
      });
      if (matchingRow) {
        console.log('[useAnalysisData] Found matching row in data_update, updating');
        // Directly update data instead of refetching
        finishLoading(matchingRow);
      } else {
        console.log('[useAnalysisData] No matching row in data_update');
      }
    };

    const handleConnect = () => {
      console.log('[useAnalysisData] Socket connected, requesting data for view:', viewId, 'pillar:', pillarId);
      lastSocketUpdateRef.current = Date.now();
      socket.emit('request_data', {
        view_id: Number(viewId),
        pillar_id: Number(pillarId)
      });
    };

    // If already connected, request data immediately
    if (socket.connected) {
      handleConnect();
    }

    socket.on('connect', handleConnect);
    socket.on('analysis_update', handleAnalysisUpdate);
    socket.on('data_update', handleDataUpdate);

    return () => {
      // Only remove listeners, don't disconnect shared socket
      socket.off('connect', handleConnect);
      socket.off('analysis_update', handleAnalysisUpdate);
      socket.off('data_update', handleDataUpdate);
      socketRef.current = null;
    };
  }, [viewId, pillarId, finishLoading]);

  return {
    data: analysisData,
    isLoading,
    error,
    refreshData: fetchData, // Or debouncedFetchData if preferred for manual refresh button
    loadAnalysisById
  };
}
